import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

const today = () => new Date().toISOString().slice(0, 10);

export default function DailyBias() {
  const { user } = useAuth();
  const [biases, setBiases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const empty = {
    bias_date: today(),
    bias: 'Bullish',
    key_levels: '',
    liquidity_targets: '',
    news_events: '',
    plan_notes: '',
    actual_outcome: '',
    reviewed: false
  };

  const [form, setForm] = useState({ ...empty });
  const [editingId, setEditingId] = useState(null);

  const fetchBiases = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('daily_bias')
      .select('*')
      .eq('user_id', user.id)
      .order('bias_date', { ascending: false });
    setLoading(false);
    if (!error) setBiases(data || []);
  }, [user.id]);

  useEffect(() => { fetchBiases(); }, [fetchBiases]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setSuccess('');

    const payload = {
      user_id: user.id,
      bias_date: form.bias_date,
      bias: form.bias,
      key_levels: form.key_levels || null,
      liquidity_targets: form.liquidity_targets || null,
      news_events: form.news_events || null,
      plan_notes: form.plan_notes || null,
      actual_outcome: form.actual_outcome || null,
      reviewed: form.reviewed
    };

    let res;
    if (editingId) {
      res = await supabase.from('daily_bias').update(payload).eq('id', editingId);
    } else {
      res = await supabase.from('daily_bias').upsert(payload, { onConflict: 'user_id,bias_date' });
    }

    if (res.error) {
      setError('Failed: ' + res.error.message);
      return;
    }
    setSuccess(editingId ? 'Bias updated.' : 'Daily bias saved.');
    setForm({ ...empty });
    setEditingId(null);
    fetchBiases();
    setTimeout(() => setSuccess(''), 3000);
  }

  function editBias(b) {
    setForm({
      bias_date: b.bias_date,
      bias: b.bias || 'Bullish',
      key_levels: b.key_levels || '',
      liquidity_targets: b.liquidity_targets || '',
      news_events: b.news_events || '',
      plan_notes: b.plan_notes || '',
      actual_outcome: b.actual_outcome || '',
      reviewed: b.reviewed
    });
    setEditingId(b.id);
    window.scrollTo({top:0, behavior:'smooth'});
  }

  async function deleteBias(id) {
    if (!window.confirm('Delete this bias entry?')) return;
    await supabase.from('daily_bias').delete().eq('id', id);
    fetchBiases();
  }

  const reviewed = biases.filter(b => b.reviewed);
  const correct = reviewed.filter(b => b.actual_outcome === 'Correct').length;
  const accuracy = reviewed.length ? ((correct / reviewed.length) * 100).toFixed(1) : '—';

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Daily <em>Bias Planner</em></h1>
        <p className="page-subtitle">Pre-market planning and post-market review — track your bias accuracy over time</p>
      </div>

      {/* STATS */}
      <div className="kpi-grid">
        <div className="kpi-cell">
          <div className="kpi-label">TOTAL BIASES</div>
          <div className="kpi-value">{biases.length}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">REVIEWED</div>
          <div className="kpi-value">{reviewed.length}</div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">BIAS ACCURACY</div>
          <div className="kpi-value" style={{color: parseFloat(accuracy) >= 60 ? 'var(--green)' : 'var(--gold-2)'}}>
            {accuracy}{reviewed.length ? '%' : ''}
          </div>
        </div>
        <div className="kpi-cell">
          <div className="kpi-label">CURRENT STREAK</div>
          <div className="kpi-value">{(() => {
            let s = 0;
            for (const b of reviewed) {
              if (b.actual_outcome === 'Correct') s++;
              else break;
            }
            return s;
          })()}</div>
        </div>
      </div>

      {/* FORM */}
      <div className="card mb-24">
        <div className="card-title">{editingId ? 'EDIT BIAS' : 'LOG TODAY\'S BIAS'}</div>
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}
        <form className="journal-form" onSubmit={handleSubmit}>
          <div className="field">
            <label>DATE</label>
            <input type="date" value={form.bias_date} onChange={e => setForm(f => ({...f, bias_date: e.target.value}))} required />
          </div>
          <div className="field">
            <label>BIAS</label>
            <select value={form.bias} onChange={e => setForm(f => ({...f, bias: e.target.value}))}>
              <option value="Bullish">Bullish</option>
              <option value="Bearish">Bearish</option>
              <option value="Neutral">Neutral / Range</option>
            </select>
          </div>
          <div className="field full">
            <label>KEY LEVELS (support, resistance, prior session H/L)</label>
            <input type="text" placeholder="e.g. R: 2345 / 2360 · S: 2310 / 2298 · PDH: 2342 · PDL: 2305" value={form.key_levels} onChange={e => setForm(f => ({...f, key_levels: e.target.value}))} />
          </div>
          <div className="field full">
            <label>LIQUIDITY TARGETS</label>
            <input type="text" placeholder="e.g. Asia high at 2338, equal lows at 2308" value={form.liquidity_targets} onChange={e => setForm(f => ({...f, liquidity_targets: e.target.value}))} />
          </div>
          <div className="field full">
            <label>NEWS / RED FOLDER EVENTS</label>
            <input type="text" placeholder="e.g. CPI 12:30 UTC, Powell speech 18:00 UTC" value={form.news_events} onChange={e => setForm(f => ({...f, news_events: e.target.value}))} />
          </div>
          <div className="field full">
            <label>PLAN NOTES</label>
            <textarea placeholder="Trade plan, conditions to enter, what would invalidate the bias..." value={form.plan_notes} onChange={e => setForm(f => ({...f, plan_notes: e.target.value}))} />
          </div>
          <div className="field">
            <label>ACTUAL OUTCOME (review EOD)</label>
            <select value={form.actual_outcome} onChange={e => setForm(f => ({...f, actual_outcome: e.target.value, reviewed: e.target.value ? true : false}))}>
              <option value="">— Not yet reviewed —</option>
              <option value="Correct">Correct</option>
              <option value="Wrong">Wrong</option>
              <option value="No Trade">No Trade Taken</option>
            </select>
          </div>
          <div className="field" style={{alignSelf:'end'}}>
            <button type="submit" className="btn-gold">{editingId ? 'Update Bias' : 'Save Bias'}</button>
            {editingId && (
              <button type="button" className="btn-sm ghost" onClick={() => { setEditingId(null); setForm({...empty}); }} style={{marginTop:8}}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* HISTORY */}
      <div className="card">
        <div className="card-title">BIAS HISTORY</div>
        {loading ? (
          <div style={{textAlign:'center',padding:24,color:'var(--text-3)'}}>Loading...</div>
        ) : biases.length === 0 ? (
          <div style={{textAlign:'center',padding:32,color:'var(--text-4)'}}>
            <div style={{fontSize:32,marginBottom:8}}>🧭</div>
            <p>No biases logged yet. Use the form above to log today's plan.</p>
          </div>
        ) : (
          <div className="trades-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Bias</th>
                  <th>Key Levels</th>
                  <th>News</th>
                  <th>Outcome</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {biases.map(b => (
                  <tr key={b.id}>
                    <td className="td-mono">{b.bias_date}</td>
                    <td>
                      <span className={`badge ${b.bias === 'Bullish' ? 'green' : b.bias === 'Bearish' ? 'red' : 'grey'}`}>
                        {b.bias}
                      </span>
                    </td>
                    <td style={{maxWidth:240,overflow:'hidden',textOverflow:'ellipsis',fontSize:12,color:'var(--text-3)'}}>{b.key_levels || '—'}</td>
                    <td style={{maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',fontSize:12,color:'var(--text-3)'}}>{b.news_events || '—'}</td>
                    <td>
                      {b.actual_outcome ? (
                        <span className={`badge ${b.actual_outcome === 'Correct' ? 'green' : b.actual_outcome === 'Wrong' ? 'red' : 'grey'}`}>
                          {b.actual_outcome}
                        </span>
                      ) : (
                        <span className="badge amber">Pending</span>
                      )}
                    </td>
                    <td style={{display:'flex',gap:6}}>
                      <button className="btn-sm ghost" onClick={() => editBias(b)}>Edit</button>
                      <button className="btn-sm danger" onClick={() => deleteBias(b.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
